/**
 * Slides down a target element by animating its height, padding, and margin properties.
 *
 * This function first sets the display of the target element to block if it's not already displayed.
 * It then animates the height from 0 to the target's full height over the specified duration.
 * After the animation, it resets the overflow and transition properties.
 *
 * @param {HTMLElement} target - The target element to animate.
 * @param {number} [duration=500] - The duration of the animation in milliseconds. Defaults to 500ms.
 */
const slideDown = (target, duration = 500) => {
  target.style.removeProperty('display');

  // Check the current display style and set to block if none
  let display = window.getComputedStyle(target).display;
  if (display === 'none') {
    display = 'block';
  }
  target.style.display = display;

  // Get the full height of the element
  const height = target.offsetHeight;

  // Set initial styles for animation
  target.style.overflow = 'hidden';
  target.style.height = 0;
  target.style.paddingTop = 0;
  target.style.paddingBottom = 0;
  target.style.marginTop = 0;
  target.style.marginBottom = 0;

  // Trigger reflow to apply the height of 0
  target.offsetHeight; // This forces the browser to calculate styles

  // Set the box-sizing and transition properties
  target.style.boxSizing = 'border-box';
  target.style.transitionProperty = 'height, margin, padding';
  target.style.transitionDuration = `${duration}ms`;

  // Set the height to animate to the full height
  target.style.height = `${height}px`;

  // Reset padding and margin to original values
  target.style.removeProperty('padding-top');
  target.style.removeProperty('padding-bottom');
  target.style.removeProperty('margin-top');
  target.style.removeProperty('margin-bottom');

  // Cleanup after the animation duration
  window.setTimeout(() => {
    target.style.removeProperty('height');
    target.style.removeProperty('overflow');
    target.style.removeProperty('transition-duration');
    target.style.removeProperty('transition-property');
  }, duration);
};

export default slideDown;
